/// <amd-module name="Wit/HtmlEditor/Plugins/WorkItemLinkPlugin" />

import { Editor, EditorPlugin, PluginEvent, PluginEventType } from "roosterjs-react";
import { IVssPageContext } from "VSS/Platform/Context";
import { makeWorkItemHyperlinkClickable, revertWorkItemHyperlinks } from "../Utils/WorkItemLinks";

/**
 * This plugin is for rendering special work item links in HTML fields.
 *
 * It will convert anchor tags that look like this:
 *     <a href="x-mvwit:workitem/6957">Task 6957</a>
 *
 * into anchor tags that look like this:
 *     <a href="https://codedev.ms/test-org/_workitems/edit/6957" data-html-editor-org-href="x-mvwit:workitem/6957">Task 6957</a>
 *
 * See Tfs\Service\WebAccess\WorkItemTracking\Scripts\Components\HtmlEditorLinkPlugin.ts
 * for the old-web-platform implementation, which also handled vstfs:/// links.
 */
export class WorkItemLinkPlugin implements EditorPlugin {
    private _pageContext: IVssPageContext;
    private _editor: Editor | null;

    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
        this._editor = null;
    }

    public getName(): string {
        return "WorkItemLinkPlugin";
    }

    public initialize(editor: Editor): void {
        this._editor = editor;
    }

    public dispose(): void {
        this._editor = null;
    }

    public onPluginEvent(event: PluginEvent): void {
        // On content change and editor ready, we'll replace all x-vwit and VSTFS links with final links
        // so that user can CTRL+Click and right click new window. When extracting content for saving,
        // we want to go back to the original links.
        const editorReadyEvent = event.eventType === PluginEventType.EditorReady;
        if (event.eventType === PluginEventType.ContentChanged || editorReadyEvent) {
            // When the content loads for the first time or when the content changes, modify urls to make the links clickable
            this._editor?.queryElements("a[href]", (anchor: HTMLAnchorElement) => {
                makeWorkItemHyperlinkClickable(anchor, this._pageContext);
            });
        } else if (event.eventType === PluginEventType.ExtractContent) {
            // When content is extracted from the editor, return the extracted content to its raw format
            event.content = revertWorkItemHyperlinks(event.content);
        }
    }
}
